import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import React, { useState } from "react";
import { ChartTooltip } from "./ChartTooltip";
import { Box, Typography } from "@mui/material";
import { TableDataEntity } from "../FeedbackSelectedTarget";
import { makeStyles } from "@mui/styles";

interface singleBarData {
  dataKey: string;
  fill: string;
}

interface legendProps {
  [key: string]: string;
}

interface BarGraphProps {
  data: TableDataEntity[] | undefined;

  xAxisDataKey: string;
  xAxisType: "number" | "category" | undefined;

  yAxisDataKey: string;
  yAxisType: "number" | "category" | undefined;
  stackBar: singleBarData[];
  legend: legendProps;
}

const axisStyle = {
  normal: {
    font: "normal normal normal 16px/24px Roboto",
  },
  hover: {
    font: "normal normal normal 16px/24px Roboto",
    fill: "#191919",
  },
};

export interface BarChartData {
  questionId: string;
  questionName: string;
  order: number;
  feedbackHintSubmitted: number;
  feedbackHintUnsubmitted: number;
  feedbackErrorSubmitted: number;
  feedbackErrorUnsubmitted: number;
  feedbackHintTotal: number;
  feedbackErrorTotal: number;
  x: number;
  y: number;
  width: number;
  height: number;
  value?: number[] | null;
  payload?: Payload;
  background: Background;
  tooltipPayload?: TooltipPayloadEntity[] | null;
  tooltipPosition: TooltipPosition;
}

export interface Payload {
  questionId: string;
  questionName: string;
  order: number;
  feedbackHintSubmitted: number;
  feedbackHintUnsubmitted: number;
  feedbackErrorSubmitted: number;
  feedbackErrorUnsubmitted: number;
  feedbackHintTotal: number;
  feedbackErrorTotal: number;

  [key: string]: any;
}

export interface Background {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TooltipPayloadEntity {
  fill: string;
  dataKey: string;
  name: string;
  color: string;
  value: number;
  payload: Payload;
}

export interface TooltipPosition {
  x: number;
  y: number;
}

const useStyle = makeStyles((theme) => ({
  container: {
    [theme.breakpoints.down("lg")]: {
      marginBottom: "8px",
    },
    [theme.breakpoints.up("lg")]: {
      marginBottom: "128px",
    },
  },
}));

const CustomisedYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const [hover, setHover] = React.useState(false);
  return (
    <g transform={`translate(${x},${y - 10})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#5D5D5D"
        style={{ ...axisStyle.normal, ...(hover ? axisStyle.hover : {}) }}
        onMouseEnter={() => {
          setHover(true);
        }}
        onMouseLeave={() => {
          setHover(false);
        }}>
        {payload.value}
      </text>
    </g>
  );
};
export const BarGraph = (props: BarGraphProps) => {
  const { data, xAxisDataKey, xAxisType, yAxisDataKey, yAxisType, stackBar, legend } =
    props;
  let [posData, setPosData] = useState({} as BarChartData);
  const classes = useStyle();
  return (
    <Box className={classes.container}>
      <ResponsiveContainer height={250} width="99%" aspect={3}>
        {/* The 99.9% above gets around a responsivness bug with Recharts package */}
        <BarChart
          barSize={30}
          maxBarSize={30}
          layout="vertical"
          data={data}
          margin={{
            left: -30,
          }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis dataKey={xAxisDataKey} type={xAxisType} />
          <YAxis
            tickLine={false}
            type={yAxisType}
            dataKey={yAxisDataKey}
            fontSize="16"
            width={175}
            tick={<CustomisedYAxisTick />}
          />
          <Tooltip
            wrapperStyle={{ outline: "none", marginLeft: "8px" }}
            content={
              <ChartTooltip title={posData.questionName}>
                <Typography variant="body1" sx={{ fontSize: "16px", fontWeight: "700" }}>
                  {posData.payload ? posData.payload[xAxisDataKey] : ""} total
                  {xAxisDataKey.includes("Hint") ? " hints" : " errors"}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {Object.entries(legend).map(([key, value], index) => {
                    return (
                      <Typography
                        key={`bar-legend-${index}-${key}`}
                        variant="body2"
                        sx={{ fontSize: "14px" }}>
                        {posData.payload ? posData.payload[key] : ""} {value}
                      </Typography>
                    );
                  })}
                </Box>
              </ChartTooltip>
            }
            position={{ x: posData.x + posData.width / 2, y: posData.y - 150 }}
            cursor={{ fill: "transparent" }}
          />
          <Legend
            iconType={"circle"}
            formatter={(value, entry, index) => {
              return <span style={{ color: "#000" }}>{legend[value]}</span>;
            }}
          />
          {stackBar.map((bar, index) => {
            return (
              <Bar
                onMouseOver={(data) => {
                  setPosData(data);
                }}
                dataKey={bar.dataKey}
                fill={bar.fill}
                stackId="a"
                key={`bar-${index}`}></Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
